import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import type { BandwidthActivityByWeekPoint } from './bandwidthDashboardTypes';
import { formatAxisCompactK, formatDataLabelCompact } from '../kpi-dashboard/chartFormatters';
import {
  bandwidthVerticalBarDim,
  bandwidthVerticalBarPalette,
  bandwidthVerticalBarSelected,
} from './bandwidthChartTheme';
import { niceCountAxisMax } from './bandwidthChartMath';

export interface BandwidthActivityByWeekChartProps {
  title: string;
  subtitle?: string;
  points: BandwidthActivityByWeekPoint[];
  /** UTC Monday YYYY-MM-DD when a week bar is selected (ISO week drill, Weekwise tab). */
  selectedWeekStart?: string | null;
  onWeekBarClick?: (weekStartYyyyMmDd: string) => void;
  emptyHint?: string;
}

const BandwidthActivityByWeekChart: React.FC<BandwidthActivityByWeekChartProps> = ({
  title,
  subtitle,
  points,
  selectedWeekStart = null,
  onWeekBarClick,
  emptyHint,
}) => {
  const { labels, counts, barColors, yMax } = useMemo(() => {
    const labelsInner = points.map((p) => p.weekLabel);
    const countsInner = points.map((p) => p.activityCount);
    const maxC = countsInner.length ? Math.max(...countsInner) : 0;
    const yMaxInner = niceCountAxisMax(maxC);
    const colorsInner = points.map((p, i) => {
      if (!selectedWeekStart) return bandwidthVerticalBarPalette(i);
      return p.weekStart === selectedWeekStart ? bandwidthVerticalBarSelected : bandwidthVerticalBarDim;
    });
    return { labels: labelsInner, counts: countsInner, barColors: colorsInner, yMax: yMaxInner };
  }, [points, selectedWeekStart]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 20, right: 8, left: 4, bottom: 4 } },
      onClick: (_event: unknown, elements: { index: number }[]) => {
        if (!onWeekBarClick || elements.length === 0) return;
        const idx = elements[0]?.index;
        const point = points[idx];
        if (!point) return;
        onWeekBarClick(point.weekStart);
      },
      onHover: (_event: unknown, elements: unknown[], chart: { canvas?: HTMLCanvasElement }) => {
        const canvas = chart?.canvas;
        if (!canvas) return;
        canvas.style.cursor =
          onWeekBarClick && Array.isArray(elements) && elements.length > 0 ? 'pointer' : 'default';
      },
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed: { y: number | null } }) => {
              const v = ctx.parsed.y;
              return typeof v === 'number' ? `${v.toLocaleString()} activities` : '';
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: '#64748b',
            font: { size: 10 },
            maxRotation: 45,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          max: yMax,
          grid: { color: 'rgba(99, 102, 241, 0.14)' },
          ticks: {
            color: '#64748b',
            font: { size: 10 },
            callback: (value: string | number) => formatAxisCompactK(Number(value)),
          },
          title: { display: true, text: 'Activity count', color: '#64748b', font: { size: 11 } },
        },
      },
    }),
    [points, onWeekBarClick, yMax]
  );

  if (points.length === 0) {
    return (
      <KpiChartPanel
        title={title}
        titleClassName="!pl-0"
        className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
        flexFill
        bodyClassName="px-3 pb-4 pt-4"
      >
        {subtitle ? <p className="mb-2 text-xs text-slate-500">{subtitle}</p> : null}
        <p className="py-12 text-center text-sm text-slate-500">
          {emptyHint ?? 'No data for the selected filters.'}
        </p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
      flexFill
      bodyClassName="px-3 pb-3 pt-3"
    >
      {subtitle ? <p className="mb-1 text-xs leading-snug text-slate-500">{subtitle}</p> : null}
      <p className="mb-1 text-[11px] text-slate-500">
        {onWeekBarClick
          ? 'Click a week to filter the other charts. Click the same bar again to clear.'
          : null}
      </p>
      <div className="relative h-[260px] w-full min-h-0 overflow-x-auto sm:h-[280px]">
        <div className="min-w-[min(100%,520px)] h-full">
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: 'Activity count',
                  data: counts,
                  backgroundColor: barColors,
                  borderWidth: 0,
                  borderRadius: 4,
                  maxBarThickness: 36,
                  datalabels: {
                    display: true,
                    anchor: 'end',
                    align: 'end',
                    offset: 2,
                    clip: false,
                    color: '#334155',
                    font: { size: 10, weight: 600 },
                    formatter: (value: number) => formatDataLabelCompact(value),
                  },
                },
              ],
            }}
            options={chartOptions}
          />
        </div>
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthActivityByWeekChart;
